import { useCallback, useEffect, useState } from "react";
import { Copy, Loader2, RefreshCw, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { getAffiliateDashboard } from "@/lib/api";
import { formatRupiah } from "@/lib/site";

const STATUS = {
  PENDING: "Menunggu persetujuan", APPROVED: "Disetujui", PAID: "Dibayar",
  REJECTED: "Ditolak", CANCELLED: "Dibatalkan", PREPARING: "Disiapkan",
  READY: "Siap dibayar", BLOCKED: "Perlu diperiksa",
};
const date = (value) => value ? new Date(value).toLocaleDateString("id-ID") : "—";

export default function Affiliate() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try { setData(await getAffiliateDashboard()); }
    catch (e) { setError(e?.response?.data?.detail || "Gagal memuat dashboard affiliate."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const referralLink = data?.referral_link ? new URL(data.referral_link, window.location.origin).href : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      toast.success("Link referral disalin.");
    } catch { toast.error("Link belum dapat disalin. Salin dari kolom link referral."); }
  };

  const metrics = data?.metrics || {};
  const cards = [
    ["Klik referral", metrics.clicks || 0], ["Pendaftaran", metrics.registrations || 0],
    ["Pembeli", metrics.buyers || 0], ["Penjualan", formatRupiah(metrics.sales || 0)],
    ["Komisi pending", formatRupiah(metrics.pending_commission || 0)],
    ["Komisi disetujui", formatRupiah(metrics.approved_commission || 0)],
    ["Komisi dibayar", formatRupiah(metrics.paid_commission || 0)],
    ["Komisi dibatalkan", formatRupiah(metrics.cancelled_commission || 0)],
  ];

  return (
    <div className="mx-auto max-w-6xl space-y-7 p-4 md:p-8" data-testid="affiliate-dashboard">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-blue-600">Referral</p>
          <h1 className="mt-2 font-heading text-3xl font-bold text-slate-900">Affiliate Saya</h1>
        </div>
        <Button variant="outline" disabled={loading} onClick={load} aria-label="Muat ulang dashboard affiliate">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>
      {error && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</p>}
      {loading && !data ? <div className="flex justify-center gap-2 py-12 text-slate-500"><Loader2 className="h-5 w-5 animate-spin" /> Memuat...</div> : data && <>
        <div className="rounded-2xl border border-blue-100 bg-blue-50 p-6">
          <div className="flex items-center gap-2 font-semibold text-blue-900"><Users className="h-5 w-5" /> Program affiliate {data.settings.enabled ? "aktif" : "nonaktif"}</div>
          <p className="mt-2 text-sm text-slate-600">
            {data.settings.enabled ? `Komisi saat ini ${data.settings.commission_percentage}% untuk pesanan referral baru yang memenuhi syarat. Jendela atribusi ${data.settings.attribution_days} hari.` : "Program sedang dinonaktifkan. Riwayat komisi dan pembayaran Anda tetap tersedia."}
          </p>
          <label htmlFor="affiliate-referral-link" className="mt-4 block text-sm font-medium text-slate-700">Link referral · {data.referral_code}</label>
          <div className="mt-2 flex flex-col gap-2 sm:flex-row">
            <input id="affiliate-referral-link" readOnly value={referralLink} onFocus={(e) => e.target.select()}
              className="min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" />
            <Button onClick={copyLink} disabled={!data.settings.enabled || !referralLink}><Copy className="mr-2 h-4 w-4" /> Salin link</Button>
          </div>
          <p className="mt-3 text-xs text-slate-500">Pembayaran komisi dicatat manual oleh admin. Minimum payout {formatRupiah(data.settings.minimum_payout)}. Nilai komisi mengikuti pengaturan saat transaksi dibuat.</p>
        </div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {cards.map(([label, value]) => <div key={label} className="rounded-xl border border-slate-200 bg-white p-4"><p className="text-xs text-slate-500">{label}</p><p className="mt-2 text-xl font-bold text-slate-900">{value}</p></div>)}
        </div>
        <section className="space-y-3">
          <h2 className="font-heading text-xl font-semibold text-slate-900">Riwayat komisi</h2>
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-left text-sm"><thead className="bg-slate-50 text-slate-500"><tr><th className="p-3">Tanggal</th><th className="p-3">Paket</th><th className="p-3">Transaksi</th><th className="p-3">Komisi</th><th className="p-3">Status</th></tr></thead>
              <tbody>{data.commissions.map((row) => <tr key={row.id || row._id} className="border-t border-slate-100"><td className="p-3 whitespace-nowrap">{date(row.created_at)}</td><td className="p-3">{row.package_name || row.package_code}</td><td className="p-3 whitespace-nowrap">{formatRupiah(row.transaction_amount)}</td><td className="p-3 whitespace-nowrap">{formatRupiah(row.commission_amount)} <span className="text-xs text-slate-400">({row.commission_rate}%)</span></td><td className="p-3">{STATUS[row.status] || row.status}{row.reversal_needs_review && <p className="mt-1 text-xs text-amber-700">Pembalikan pembayaran perlu diperiksa</p>}</td></tr>)}
                {!data.commissions.length && <tr><td colSpan={5} className="p-8 text-center text-slate-500">Belum ada komisi.</td></tr>}
              </tbody></table>
          </div>
        </section>
        <section className="space-y-3">
          <h2 className="font-heading text-xl font-semibold text-slate-900">Riwayat payout</h2>
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-left text-sm"><thead className="bg-slate-50 text-slate-500"><tr><th className="p-3">Tanggal</th><th className="p-3">Jumlah</th><th className="p-3">Status</th><th className="p-3">Referensi transfer</th></tr></thead><tbody>
              {data.payouts.map((row) => <tr key={row.id || row._id} className="border-t border-slate-100"><td className="p-3">{date(row.paid_at || row.created_at)}</td><td className="p-3 whitespace-nowrap">{formatRupiah(row.amount)}</td><td className="p-3">{STATUS[row.status] || row.status}</td><td className="p-3 break-all">{row.reference || "—"}</td></tr>)}
              {!data.payouts.length && <tr><td colSpan={4} className="p-8 text-center text-slate-500">Belum ada payout.</td></tr>}
            </tbody></table>
          </div>
        </section>
      </>}
    </div>
  );
}
